import { useEffect, useRef, useState } from "react";
import { Zoom, toast } from "react-toastify";
import { ReturnViewMode } from "..";
import { returnToastStyle } from "../eQueueUtils";
import {
	formatElapsedSeconds,
	getPauseTotalSeconds,
	getPauseTotalStorageKey
} from "../pauseTimeStorage";

const makeElapsedTimer = (
	startTime: string | undefined,
	setter: (v: string | undefined) => void
): ReturnType<typeof setInterval> | undefined => {
	if ( !startTime ) { setter(undefined); return undefined; }
	const tick = () => {
		const diffSec = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
		setter(formatElapsedSeconds(diffSec));
	};
	tick();
	return setInterval(tick, 1000);
};

interface UseReturnTimersParams {
	isReturnQueue: boolean;
	viewMode: ReturnViewMode | undefined;
	pauseStartTime: string | undefined;
	pauseTotalStorageKey?: string;
	ticket: { number?: string; inviteTime?: string; startTime?: string } | undefined;
}

export const useReturnTimers = ({
	isReturnQueue,
	viewMode,
	pauseStartTime,
	pauseTotalStorageKey,
	ticket
}: UseReturnTimersParams) => {
	const [ pauseElapsed, setPauseElapsed ] = useState<string | undefined>(undefined);
	const [ pauseTotalElapsed, setPauseTotalElapsed ] = useState<string | undefined>(undefined);

	useEffect(() => {
		if ( !isReturnQueue || viewMode !== "pause" ) {
			setPauseElapsed(undefined);
			setPauseTotalElapsed(undefined);
			return;
		}

		const baseSeconds = getPauseTotalSeconds(pauseTotalStorageKey);
		const tick = () => {
			if ( !pauseStartTime ) {
				setPauseElapsed(undefined);
				setPauseTotalElapsed(formatElapsedSeconds(baseSeconds));
				return;
			}
			const currentSeconds = Math.floor((Date.now() - new Date(pauseStartTime).getTime()) / 1000);
			setPauseElapsed(formatElapsedSeconds(currentSeconds));
			setPauseTotalElapsed(formatElapsedSeconds(baseSeconds + currentSeconds));
		};

		tick();
		const id = setInterval(tick, 1000);
		return () => clearInterval(id);
	}, [ isReturnQueue, viewMode, pauseStartTime, pauseTotalStorageKey ]);

	const [ callElapsed, setCallElapsed ] = useState<string | undefined>(undefined);
	const noShowToastFired = useRef(false);
	useEffect(() => {
		if ( !isReturnQueue || viewMode !== "called" ) {
			setCallElapsed(undefined);
			noShowToastFired.current = false;
			return;
		}
		const timerId = makeElapsedTimer(ticket?.inviteTime, setCallElapsed);
		const inviteTime = ticket?.inviteTime;
		const ticketNumber = ticket?.number;
		let toastId: ReturnType<typeof setTimeout> | undefined;
		if ( inviteTime ) {
			const remaining = 7 * 60 * 1000 - (Date.now() - new Date(inviteTime).getTime());
			if ( remaining > 0 ) {
				toastId = setTimeout(() => {
					if ( noShowToastFired.current ) return;
					noShowToastFired.current = true;
					toast.success(`Посетитель с талоном ${ticketNumber} не явился`, {
						position: "top-center",
						autoClose: 10000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						transition: Zoom,
						closeButton: false,
						style: returnToastStyle
					});
				}, remaining);
			}
		}
		return () => {
			if (timerId) clearInterval(timerId);
			if (toastId) clearTimeout(toastId);
		};
	}, [ isReturnQueue, viewMode, ticket?.inviteTime ]);

	const [ serviceElapsed, setServiceElapsed ] = useState<string | undefined>(undefined);
	useEffect(() => {
		if ( !isReturnQueue || viewMode !== "working" ) { setServiceElapsed(undefined); return; }
		const id = makeElapsedTimer(ticket?.startTime, setServiceElapsed);
		return () => { if (id) clearInterval(id); };
	}, [ isReturnQueue, viewMode, ticket?.startTime ]);

	return { pauseElapsed, pauseTotalElapsed, callElapsed, serviceElapsed };
};
